import { AppRegistrationSection } from '../../AppRegistrationSection';
import {
  ArrowLeftIcon,
  BankStatementSampleIcon,
  DniFrontBackSampleIcon,
  PlusIcon,
} from 'components/icons';
import { ButtonActor, Countdown, Heading, LinkActor } from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { JUICY_NEXT_BTN } from 'utils/constants';
import { routes } from '../../routes';
import { useEffect } from 'react';
import { useGetClient } from '../../api';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import Card from 'components/atoms/Card/Card';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import Link from 'components/atoms/Link/Link';
import Spacer from 'components/atoms/Spacer/Spacer';
import styles from './RegistrationEmailDoc.module.scss';
import Text from 'components/atoms/Typography/Text';
import Title from 'components/atoms/Title/Title';

export default function RegistrationEmailDoc() {
  const { fetch: fetchGetClient } = useGetClient();
  const navigate = useNavigate();
  const location = useLocation();
  const i18Next = useTranslation();

  useEffect(() => {
    fetchGetClient();
  }, []);

  const handleBack = () => {
    const searchParams = new URLSearchParams(location.search);
    const backRouteUrlParam = searchParams.get('backRoute');

    if (backRouteUrlParam) navigate(backRouteUrlParam);
    else navigate(routes.FirstLoan_Manual);
  };

  const handleContinue = () => {
    switch (location.pathname) {
      case routes.FirstLoan_Tink_Email:
        navigate(routes.FirstLoan_Tink_Email_BankAccount);
        return;
      case routes.FirstLoan_Tink_Email_BankAccount:
        navigate(routes.FirstLoan_Tink_Email_Pending);
        return;
      case routes.FirstLoan_Manual_Email:
        navigate(routes.FirstLoan_Manual_Email_BankAccount);
        return;
      default:
        navigate(routes.FirstLoan_Tink_Email_Pending);
        return;
    }
  };

  const content = getScreenTranslation('register_email_documentation', i18Next);

  return (
    <Frame
      {...frameConfig}
      hideEditButton={true}
      sectionID={AppRegistrationSection.Confirmation}
      onBackBtnHandler={handleBack}
      bodyWrapperClassName={classes(
        layout['width-lg'],
        layout['frame-x-centered'],
      )}
    >
      <div
        className={classes(
          styles['title-description-wrapper'],
          layout['width-md'],
        )}
      >
        <Title className={styles.title} text={content.title} />
        <Text
          className={styles['description']}
          dangerouslySetInnerHTML={{ __html: content.subtitle }}
          dataTest="description"
          level={2}
          weight="regular"
        />
      </div>

      <Card className={styles['wrapper-card']}>
        <div className={classes(styles['container-center'])}>
          <Text
            dataTest="card-title"
            text={content.cardTitle}
            level={2}
            weight="regular"
            className={styles['text-color2']}
          />
          <Link href={`mailto:${content.email}`}>
            <Heading
              dataTest="header-email"
              level="h3"
              text={content.email}
              weight="medium"
              className={styles.link}
            />
          </Link>
        </div>
        <div className={styles['card-images']}>
          <span
            className={classes(styles['card-image'], styles['helper-text'])}
          >
            <DniFrontBackSampleIcon className={styles['dni-icon']} />
            <Text kind="p" text={content.helperDNI} level={3} />
          </span>
          <Spacer paddingHorizontal={{ default: '20px', m: '10px' }}>
            <PlusIcon className={styles['plus-icon']} />
          </Spacer>
          <span
            className={classes(styles['card-image'], styles['helper-text'])}
          >
            <BankStatementSampleIcon className={styles['bank-icon']} />
            <Text kind="p" text={content.helperReceipt} level={3} />
          </span>
        </div>
        <div className={styles['container-center']}>
          <Heading
            dataTest="header-subtitle"
            level="h3"
            text={content.cardSubtitle}
            weight="medium"
            className={styles['card-sub-title']}
          />
        </div>
        <div className={styles['timer-wrapper']}>
          <Countdown date={new Date(Date.now() + 3 * 60 * 60 * 24 * 1000)} />
        </div>
        <div
          className={classes(
            styles['container-center'],
            styles['helper-text'],
            styles['card-helper'],
          )}
        >
          <Text
            dataTest="card-helper"
            text={content.cardHelper}
            level={3}
            className={styles['bottom-helper-text']}
          />
        </div>
      </Card>

      <ButtonActor
        buttonProps={{
          className: JUICY_NEXT_BTN,
          dataTest: 'btn-submit',
          onClick: handleContinue,
          fill: true,
          type: 'submit',
        }}
        analytics={{
          description: 'Confirm button',
        }}
        className={classes(layout['width-sm'], styles.button)}
      >
        {content.confirmBtn}
      </ButtonActor>
      <div className={styles['wrapper-buttons']}>
        <LinkActor
          linkProps={{
            dataTest: 'link-back',
            onClick: handleBack,
            leftIcon: <ArrowLeftIcon />,
            className: styles.link,
          }}
        >
          {content.backBtn}
        </LinkActor>
      </div>
    </Frame>
  );
}
